import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import GalleryLightbox from "../components/GalleryLightbox";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function OereAkupunkturPage() {
  return (
    <>
      <Helmet>
        <title>Øre-akupunktur i Risskov | Kita Akupunktur</title>
        <meta
          name="description"
          content="Målrettet øre-akupunktur i Risskov. Lindring af uro, smerter, søvnløshed og støtte til rygestop samt vægtbalance. Book en tid hos Kita i dag."
        />
        <meta property="og:title" content="Øre-akupunktur i Risskov | Kita Akupunktur" />
        <meta
          property="og:description"
          content="Målrettet øre-akupunktur i Risskov. Lindring af uro, smerter, søvnløshed og støtte til rygestop samt vægtbalance. Book en tid hos Kita i dag."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/oere-akupunktur/" />
      </Helmet>

      {/* Page Header */}
      <section className="bg-gradient-to-b from-[#FFFAF7] via-[#FFE0CF]/30 to-[#FFFAF7] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        {/* Botanical Cherry Branch Accent from /design-elements/ (cherry-2.png) */}
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-50 pointer-events-none z-10">
          <CherryBranchImg index={2} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>MIKROSYSTEM • KITA AKUPUNKTUR</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427]">
            Øre akupunktur med nænsom støtte
          </h1>
          <p className="text-base sm:text-lg text-[#593427]/80 mt-3 max-w-2xl">
            Øret som mikrosystem for kroppens signaler og ubalancer.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16 space-y-14">
        {/* Section 1: Top Split Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left / Signals & 5 Advantages */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-[#ffe0cf] border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
                <SakuraFlower className="w-4 h-4" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Kroppens signaler behandles via øret
              </h2>
            </div>
            
            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Mærker du uro, rygetrang, vægtudfordringer eller ubalancer, hvor kroppen har brug for støtte mellem behandlingerne? Øre akupunktur bruger udvalgte punkter i øret til at støtte kroppens reaktioner, og mange søger efter akupunktur øre, når de ønsker en behandling, der kan supplere almindelig kropsakupunktur.
            </p>
            
            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Hos{" "}
              <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] transition-colors">
                Kita Akupunktur
              </Link>{" "}
              i Risskov nær Lystrup, Egå og Aarhus arbejder jeg som{" "}
              <Link to="/akupunktur" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b]">
                akupunktør
              </Link>{" "}
              med små akupunkturnåle, perma nåle og ørekugler. Behandlingen tilpasses det, du kommer med, og kan blandt andet bruges ved{" "}
              <Link to="/rygestop-behandling" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b]">
                rygestop behandling
              </Link>
              ,{" "}
              <Link to="/vaegtregulering" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b]">
                vægtregulering
              </Link>{" "}
              og{" "}
              <Link to="/stress" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b]">
                stress
              </Link>
              .
            </p>

            {/* 5 fordele ved øre akupunktur */}
            <div className="glass-sakura p-6 rounded-3xl border border-[#ffe0cf] shadow-xs space-y-4">
              <div>
                <span className="text-xs uppercase tracking-wider font-semibold text-[#d74814]">
                  Målrettet støtte
                </span>
                <h3 className="text-xl sm:text-2xl font-serif text-[#593427] mt-0.5">
                  5 fordele ved øre akupunktur
                </h3>
              </div>
              <div className="space-y-2.5">
                {[
                  "Små sterile nåle eller ørekugler tilpasses din sensitivitet",
                  "Perma nåle kan sidde i flere dage for fortsat støtte i hverdagen",
                  "Særlig velegnet som supplement ved rygestop og vægtregulering",
                  "Dæmper stress, tankemylder og fysisk uro",
                  "Kombineres let med klassisk japansk akupunktur",
                ].map((bullet, idx) => (
                  <div key={idx} className="flex items-start space-x-3 bg-white/95 p-3.5 rounded-xl border border-[#ffe0cf] shadow-2xs">
                    <span className="w-5 h-5 rounded-full bg-[#d74814] text-white flex items-center justify-center text-xs font-semibold shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-[#593427] font-medium text-sm sm:text-base">{bullet}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right / Hvad er øre akupunktur card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="glass-card p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-md space-y-4 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-44 opacity-15 pointer-events-none">
                <CherryBranchImg index={4} className="w-full h-auto" />
              </div>

              <div className="flex items-center space-x-2.5 relative z-10">
                <SakuraFlower className="w-5 h-5 text-[#d74814]" />
                <h2 className="text-xl sm:text-2xl font-serif text-[#593427]">
                  Hvad er øre akupunktur?
                </h2>
              </div>
              <p className="text-[#593427]/90 leading-relaxed text-sm sm:text-base relative z-10">
                Øre akupunktur bygger på princippet om, at hele kroppen afspejler sig i øret som et mikrosystem. Ved at stimulere bestemte zoner i øret kan man påvirke organer, muskler, led og nervesystemet.
              </p>
              <p className="text-[#593427]/90 leading-relaxed text-sm sm:text-base relative z-10">
                Metoden er særdeles velegnet til at dæmpe uro, stress, sukkertrang og afhængighed, og anvendes ofte som et effektivt supplement til klassisk kropsakupunktur.
              </p>
              <div className="pt-3 border-t border-[#ffe0cf] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2.5 relative z-10">
                <JapaneseBadge label="MIKROSYSTEM" meaning="Reflekspunkter for hele kroppen" />
                <JapaneseBadge label="PERMANÅLE" meaning="Langvarig beroligende effekt" />
              </div>
            </div>
          </div>
        </div>

        {/* Section: Relaterede behandlingsområder */}
        <div className="p-6 sm:p-8 rounded-3xl bg-[#fff5ee] border border-[#ffe0cf] space-y-4">
          <div className="space-y-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#d74814]">
              Relaterede behandlingsområder
            </div>
            <p className="text-base font-serif text-[#593427]">
              Øreakupunktur anvendes alene eller som målrettet supplement til andre behandlinger
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Link
              to="/rygestop-behandling"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Rygestop behandling →
            </Link>
            <Link
              to="/vaegtregulering"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Vægtregulering →
            </Link>
            <Link
              to="/stress"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Stress & nervesystem →
            </Link>
            <Link
              to="/japansk-akupunktur"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Japansk akupunktur →
            </Link>
            <Link
              to="/akupunktur"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Klassisk kropsakupunktur →
            </Link>
          </div>
        </div>

        {/* Hurtige genveje */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2 text-xs sm:text-sm">
          <Link
            to="/priser"
            className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
          >
            Se priser & tilskud fra “danmark”
          </Link>
          <Link
            to="/om-mig"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Læs om Mayumi (Nada akupunktør)
          </Link>
          <Link
            to="/faq"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Samlet FAQ
          </Link>
          <Link
            to="/kontakt"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Bestil tid i Risskov
          </Link>
        </div>

        {/* FAQ Section sidst på siden */}
        <section className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
          <FaqSection
            title="Ofte stillede spørgsmål om øre akupunktur"
            subtitle="Få svar på de mest almindelige spørgsmål om mikrosystemet i øret, permanåle, dæmpning af sukkertrang, rygestop og smertebehandling."
            faqs={[
              {
                question: "Hvordan virker øreakupunktur på kroppen?",
                answer:
                  "Øreakupunktur (auriculoterapi) bygger på princippet om, at hele menneskekroppen og dens organer er reflekteret i ørets fine nervebaner som et mikrosystem. Når en specifik zone i øret stimuleres, sendes der neurologiske signaler direkte via kranienerverne til hjernestammen og centralnervesystemet. Dette hjælper effektivt med at dæmpe smerter, regulere stresshormoner og genskabe naturlig balance i det berørte kropsområde.",
              },
              {
                question: "Hvad er forskellen på almindelige nåle og permanente ørenåle eller metalkugler?",
                answer:
                  "Under selve konsultationen anvender jeg ofte bittesmå, sterile engangsnåle, som sidder i øret i 20-30 minutter, mens du slapper af på briksen. For at forlænge effekten i din hverdag kan der påsættes mikroskopiske permanåle eller diskrete metalkugler på et hudfarvet plaster. Disse bliver siddende i op til en uge, så du selv let kan stimulere punkterne ved et mildt fingertryk, hvis uro, sukkertrang eller smerter blusser op.",
              },
              {
                question: "Hvilke udfordringer er øreakupunktur særligt velegnet til?",
                answer: (
                  <>
                    Øreakupunktur er særdeles anerkendt og veldokumenteret til afvænning og trangdæmpning – herunder{" "}
                    <Link to="/rygestop-behandling" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      rygestop
                    </Link>
                    , sukkertrang og{" "}
                    <Link to="/vaegtregulering" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      vægtregulering
                    </Link>
                    . Derudover anvendes metoden mod smerter i{" "}
                    <Link to="/ondt-i-ryggen" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      ryg
                    </Link>{" "}
                    og{" "}
                    <Link to="/led-og-muskelsmerter" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      led
                    </Link>
                    ,{" "}
                    <Link to="/migraene" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      migræne
                    </Link>{" "}
                    samt ved{" "}
                    <Link to="/stress" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      stress og søvnløshed
                    </Link>
                    . Metoden kombineres ofte fordelagtigt med{" "}
                    <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      japansk akupunktur
                    </Link>
                    .
                  </>
                ),
              },
              {
                question: "Gør det ondt at få sat nåle eller kugler i øret?",
                answer:
                  "Da øret har en tæt nerveforsyning, kan du mærke et kortvarigt lille prik i det sekund, nålen sættes. Fornemmelsen aftager dog lynhurtigt og afløses typisk af en behagelig varme eller pulsering i øreområdet, hvilket blot vidner om øget kredsløb og frigivelse af endorfiner. Vælger vi små magnetiske metalkugler med plaster uden penetration, er behandlingen fuldstændig smertefri.",
              },
              {
                question: "Hvor længe kan de små øreplastre sidde, og hvad gør jeg derhjemme?",
                answer:
                  "De små diskrete plastre med metalkugler eller permanåle sidder normalt i 4 til 7 dage, hvorefter de enten falder af af sig selv eller let pilles af under bruseren. Du kan sagtens vaske hår, sove og dyrke sport som normalt. Skulle øret mod forventning føles ømt eller rødt, fjerner du blot plastrene med det samme og skyller øret med lunkent vand.",
              },
            ]}
          />
        </section>
      </div>
    </>
  );
}